import { Component, useState, createElement } from 'react'
import './CheckoutSheetDrawer.scss'
import { Drawer } from '@mui/material'
import classNames from 'classnames'
import { getDeviceWidth } from 'utils/utils'
import useScreenSize from '../../hooks/useScreenSize'
import PaymentManager from '../../services/PaymentManager'
import { analytics, EVENT_NAMES } from '../../services/Analytics/Analytics'
import { logger } from '../../services/logger'
import { isPaymentMethodGoogleOrApple } from '../../stores/paymentStore/payment.utils'

class CheckoutSheetContainer extends Component {
  constructor(props) {
    super(props)

    this.state = {
      paymentRequestData: null,
      shouldMakeCheckoutFullHeight: false,
    }
  }

  componentDidMount() {
    if (this.props.stripe) {
      this.canMakePaymentWthAppleOrGoogle()
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.stripe && !prevProps.stripe) {
      this.canMakePaymentWthAppleOrGoogle()
    }
  }

  setIsPaymentMethodCheckInitiated(paymentRequestState = {}) {
    this.setState({ paymentMethodCheckInitiated: true })
    analytics.track(EVENT_NAMES.CHECKOUT_CTA_LOADED, {
      'Can Make Apple Pay': paymentRequestState.canMakeApplePay,
      'Can Make Google Pay': paymentRequestState.canMakeGooglePay,
    })
  }

  canMakePaymentWthAppleOrGoogle() {
    let paymentRequestState = {}
    PaymentManager.canMakeAppleOrGooglePay({ stripe: this.props.stripe })
      .then((result) => {
        if (result.canMakePayment) {
          paymentRequestState = { paymentRequestData: result }
          if (result.canMakeApplePay) {
            paymentRequestState.canMakeApplePay = true
          }
          if (result.canMakeGooglePay) {
            paymentRequestState.canMakeGooglePay = true
          }
          this.setState(paymentRequestState)
        } else {
          const cached = PaymentManager.getSelectedPaymentMethodType()
          if (isPaymentMethodGoogleOrApple(cached)) {
            logger.info('Stale Stripe payment method detected', {
              action: 'cleared-on-mount',
              paymentMethodType: cached,
            })
            PaymentManager.clearPaymentMethod()
          }
        }
      })
      .finally(() => this.setIsPaymentMethodCheckInitiated(paymentRequestState))
  }

  render() {
    const { isRightDrawer } = this.props
    return createElement(this.props.checkoutSheetComponent, {
      ...this.props,
      rightDrawer: isRightDrawer,
      desktopReviewElegant: isRightDrawer,
      paymentRequestData: this.state.paymentRequestData,
      canMakeApplePay: this.state.canMakeApplePay,
      paymentMethodCheckInitiated: this.state.paymentMethodCheckInitiated,
      onCheckoutHeightChange: (isFullHeight) => {
        this.props.onCheckoutFullHeightChange({ isFullHeight })
      },
    })
  }
}

export function CheckoutDrawer({
  open,
  onClose,
  desktopReviewElegant,
  isRightDrawer,
  children,
  shouldMakeFullHeight = false,
  showBackdrop = true,
}) {
  if (isRightDrawer === undefined) {
    isRightDrawer = getDeviceWidth() > 960
  }

  const backdropProps = {
    invisible: !showBackdrop,
    classes: showBackdrop
      ? {
          root: 'checkout-backdrop',
        }
      : null,
  }

  return (
    <Drawer
      anchor={isRightDrawer ? 'right' : 'bottom'}
      open={open}
      onClose={onClose}
      classes={{
        paper: classNames('checkout-paper', 'no-scroll', {
          'right-drawer': isRightDrawer,
          'elegant-desktop': desktopReviewElegant,
          'full-height': shouldMakeFullHeight,
        }),
      }}
      // disableEnforceFocus prop is meant to prevent unexpected behavior when something from the drawer pop up as a modal
      // for example PayPal window shown as a modal in 'in-app browsers' like Facebook App - that prevents user to use Paypal modal (cannot type there)
      // disableScrollLock: MUI's scroll lock snapshots the body on open and re-applies it
      // when the last modal closes, making a Base UI sheet's `overflow: hidden` permanent
      // (COR-1325). Nothing locks here — the designers don't scroll and the cart sheet
      // holds its own lock.
      ModalProps={{
        disableEnforceFocus: true,
        disableScrollLock: true,
        BackdropProps: backdropProps, // Changes backdrop style
      }}
      data-testid="checkout-drawer"
    >
      {children}
    </Drawer>
  )
}

function CheckoutSheetDrawer({
  open,
  dismiss,
  desktopReviewElegant = false,
  ...checkoutSheetProps
}) {
  const [shouldMakeFullHeight, setShouldMakeFullHeight] = useState(false)
  const isRightDrawer = useScreenSize('sm')
  return (
    <CheckoutDrawer
      open={open}
      onClose={dismiss}
      desktopReviewElegant={desktopReviewElegant}
      isRightDrawer={isRightDrawer}
      shouldMakeFullHeight={shouldMakeFullHeight}
    >
      <CheckoutSheetContainer
        {...checkoutSheetProps}
        dismiss={dismiss}
        desktopReviewElegant={desktopReviewElegant}
        isRightDrawer={isRightDrawer}
        onCheckoutFullHeightChange={({ isFullHeight }) =>
          setShouldMakeFullHeight(isFullHeight)
        }
      />
    </CheckoutDrawer>
  )
}
export default CheckoutSheetDrawer
